import {
  Activity,
  AlertTriangle,
  Archive,
  Armchair,
  ArrowLeft,
  ArrowRight,
  Award,
  BadgeCheck,
  Banknote,
  BarChart3,
  BedDouble,
  Bell,
  BellOff,
  Bird,
  Bone,
  BookOpen,
  Bookmark,
  Bug,
  Building2,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarX,
  Camera,
  Car,
  Cat,
  Check,
  CheckCheck,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  CircleDashed,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CloudOff,
  Code,
  Compass,
  Copy,
  CreditCard,
  Crown,
  Dog,
  Download,
  Dumbbell,
  ExternalLink,
  Eye,
  EyeOff,
  Facebook,
  FilePlus,
  FileText,
  Filter,
  Fish,
  Flag,
  Footprints,
  Gauge,
  Gift,
  Globe,
  GraduationCap,
  Handshake,
  Headphones,
  Heart,
  HeartPulse,
  HelpCircle,
  Home,
  Hourglass,
  Image,
  ImagePlus,
  Inbox,
  IndianRupee,
  Info,
  Infinity as InfinityIcon,
  Instagram,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  Lightbulb,
  LineChart,
  Link2,
  Linkedin,
  List,
  ListChecks,
  Loader2,
  Lock,
  LogIn,
  LogOut,
  MapPin,
  Megaphone,
  Mail,
  Menu,
  MessageCircle,
  MessageCircleHeart,
  MessageSquare,
  Mic,
  Minus,
  Monitor,
  Moon,
  MoreHorizontal,
  MoreVertical,
  Navigation,
  Newspaper,
  Package,
  Palette,
  Paperclip,
  PawPrint,
  PenTool,
  Pencil,
  Percent,
  Phone,
  PhoneCall,
  PieChart,
  Pill,
  Play,
  Plug,
  Plus,
  Printer,
  Puzzle,
  Rabbit,
  RefreshCw,
  Repeat,
  Receipt,
  Rocket,
  RotateCcw,
  Route,
  Save,
  Scissors,
  Search,
  Send,
  Settings,
  Share2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShowerHead,
  Siren,
  SlidersHorizontal,
  Smile,
  Sparkles,
  SquarePen,
  Star,
  StarHalf,
  Stethoscope,
  Sun,
  Syringe,
  Tag,
  Tags,
  Target,
  ThumbsUp,
  Ticket,
  Timer,
  Trash2,
  TrendingDown,
  TrendingUp,
  Trophy,
  Twitter,
  Upload,
  User,
  UserCheck,
  UserCog,
  UserPlus,
  UserX,
  Users,
  Utensils,
  Video,
  Wallet,
  Wifi,
  WifiOff,
  X,
  XCircle,
  Youtube,
  Zap,
} from 'lucide-react';

/**
 * Semantic aliases over lucide-react.
 *
 * The point of this layer: features reference *meaning* (`booking-confirmed`),
 * not artwork (`CalendarCheck`). Swapping the underlying glyph later is a
 * one-line change here instead of a codemod across 60+ files.
 *
 * Every id in here must also have an entry in `icon-metadata.json`.
 * `npm run icons:check` fails the build if the two drift apart.
 */
export const LUCIDE_ICONS = {
  /* Navigation & shell */
  menu: Menu,
  close: X,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  back: ArrowLeft,
  forward: ArrowRight,
  external: ExternalLink,
  more: MoreVertical,
  'more-horizontal': MoreHorizontal,
  home: Home,
  dashboard: LayoutDashboard,
  'grid-view': LayoutGrid,
  'list-view': List,

  /* Discovery */
  search: Search,
  filter: Filter,
  sort: SlidersHorizontal,
  'map-pin': MapPin,
  directions: Navigation,
  explore: Compass,
  distance: Route,

  /* Pets */
  pet: PawPrint,
  'species-dog': Dog,
  'species-cat': Cat,
  'species-bird': Bird,
  'species-fish': Fish,
  'species-rabbit': Rabbit,
  treat: Bone,

  /* Service catalog — one id per SERVICE_CATALOG slug */
  'service-pet-daycare': Building2,
  'service-dog-walking': Footprints,
  'service-pet-boarding': BedDouble,
  'service-pet-grooming': Scissors,
  'service-pet-sitting': Armchair,
  'service-house-sitting': Home,
  'service-pet-training': Target,
  'service-pet-transportation': Car,
  'service-overnight-care': Moon,
  'service-veterinary-visits': Stethoscope,

  /* Care add-ons */
  'care-bath': ShowerHead,
  'care-feeding': Utensils,
  'care-exercise': Dumbbell,
  'care-medication': Pill,
  'care-vaccination': Syringe,
  'care-health-check': HeartPulse,
  'care-obedience': GraduationCap,

  /* Booking lifecycle */
  calendar: Calendar,
  'calendar-days': CalendarDays,
  'booking-confirmed': CalendarCheck,
  'booking-pending': CalendarClock,
  'booking-cancelled': CalendarX,
  clock: Clock,
  duration: Timer,
  waiting: Hourglass,
  recurring: Repeat,
  checklist: ListChecks,
  'job-sheet': ClipboardList,
  requests: Inbox,

  /* Communication */
  chat: MessageCircle,
  'chat-thread': MessageSquare,
  'chat-love': MessageCircleHeart,
  send: Send,
  mail: Mail,
  phone: Phone,
  call: PhoneCall,
  notification: Bell,
  'notification-off': BellOff,
  attachment: Paperclip,
  emoji: Smile,
  'voice-note': Mic,
  support: Headphones,

  /* People & profiles */
  user: User,
  users: Users,
  follow: UserPlus,
  'verified-user': UserCheck,
  'blocked-user': UserX,
  roles: UserCog,
  partner: Handshake,

  /* Trust, safety, reputation */
  shield: Shield,
  verified: ShieldCheck,
  risk: ShieldAlert,
  'badge-verified': BadgeCheck,
  award: Award,
  'top-rated': Crown,
  trophy: Trophy,
  rating: Star,
  'rating-half': StarHalf,
  'thumbs-up': ThumbsUp,
  favourite: Heart,
  privacy: Lock,
  'access-key': KeyRound,
  'password-show': Eye,
  'password-hide': EyeOff,
  emergency: Siren,
  'help-centre': LifeBuoy,

  /* Money */
  price: IndianRupee,
  wallet: Wallet,
  card: CreditCard,
  invoice: Receipt,
  payout: Banknote,
  coupon: Ticket,
  discount: Percent,
  'offer-tag': Tag,
  categories: Tags,
  subscription: InfinityIcon,
  reward: Gift,

  /* Data & analytics */
  'bar-chart': BarChart3,
  'pie-chart': PieChart,
  'line-chart': LineChart,
  'trend-up': TrendingUp,
  'trend-down': TrendingDown,
  'live-activity': Activity,
  performance: Gauge,
  goal: Target,

  /* Actions */
  add: Plus,
  remove: Minus,
  edit: Pencil,
  'edit-inline': SquarePen,
  write: PenTool,
  save: Save,
  delete: Trash2,
  archive: Archive,
  duplicate: Copy,
  copied: ClipboardCheck,
  share: Share2,
  'copy-link': Link2,
  upload: Upload,
  download: Download,
  print: Printer,
  refresh: RefreshCw,
  undo: RotateCcw,
  report: Flag,

  /* Status & feedback */
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
  help: HelpCircle,
  check: Check,
  'read-receipt': CheckCheck,
  pending: CircleDashed,
  dot: Circle,
  online: Wifi,
  offline: WifiOff,
  'sync-failed': CloudOff,
  'loading-fallback': Loader2,

  /* Media & content */
  photo: Image,
  'add-photo': ImagePlus,
  camera: Camera,
  video: Video,
  play: Play,
  document: FileText,
  'new-document': FilePlus,
  blog: Newspaper,
  article: BookOpen,
  bookmark: Bookmark,
  announcement: Megaphone,

  /* Social */
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
  youtube: Youtube,
  website: Globe,

  /* Settings & system */
  settings: Settings,
  theme: Palette,
  'light-mode': Sun,
  'dark-mode': Moon,
  'system-mode': Monitor,
  integrations: Plug,
  plugins: Puzzle,
  developer: Code,
  bug: Bug,
  boost: Zap,
  ai: Sparkles,
  launch: Rocket,
  tip: Lightbulb,
  package: Package,
  login: LogIn,
  logout: LogOut,
};

export const LUCIDE_ICON_IDS = Object.keys(LUCIDE_ICONS);

export default LUCIDE_ICONS;
